import React, {
  useMemo,
  useState,
  useLayoutEffect,
  useContext,
  useCallback,
  useEffect,
} from 'react'
import { Title } from '@usap/widgets'
import {
  Box,
  ResponsiveBox,
  Button,
  IcoMoon,
  useResponsiveWrapperContext,
  Text,
  CarpartsBlogIcon,
  RemoveIcon,
  AddIcon,
} from '@usap/uicomponents'
import styled from 'styled-components'
import { GTM } from '../../../helpers'
import { LocalContext } from '../../../helpers/useLocalContext'
import ItemView from '../../seoComponents/blog/item'
import homeIDs from '../../../../config/homeIds'
import BlogProvider, { BlogContext } from '../../../contexts/blogs.context'

const upperFirst = params => {
  if (typeof params === 'string') {
    return params.charAt(0).toUpperCase() + params.slice(1)
  }
  return ''
}

const CollapseBox = styled(Box)`
  cursor: pointer;
  display: none;
  @media screen and (max-width: 1024px) {
    display: block;
  }
`

const HeadingSection = styled(Box)`
  @media (max-width: 1024px) {
    flex-direction: row;
    align-self: flex-start;
    justify-content: space-between;
    padding: 0;
    margin: 0;
    width: 100%;
    align-items: center;
  }
`

const MainSection = styled(ResponsiveBox)`
  padding: 12px;
`

const BlogComponent = ({ dynamicApiData, pageType }) => {
  const { isDesktop } = useResponsiveWrapperContext()
  const { localContext } = useContext(LocalContext)
  const { isBot } = localContext
  const { blogClick } = GTM
  const blogUrl = 'https://www.carparts.com/blog/'
  const initialLimit = 3
  const blogPosts = useContext(BlogContext)
  const blogs = useMemo(
    () => dynamicApiData || blogPosts || {},
    [blogPosts, dynamicApiData],
  )

  const colWidth = isDesktop ? '33.33%' : '100%'
  const [isDisplay, setIsDisplay] = useState(true)

  const onBlogClick = useCallback(
    () => blogClick(`${upperFirst(pageType)} Page Blog Section`),
    [blogClick, pageType],
  )

  const blogsToShow = useMemo(() => {
    if (!blogs || !blogs.length) return null
    if (isBot) return blogs
    return blogs.slice(0, initialLimit)
  }, [blogs, isBot])

  useEffect(() => {
    if (isDesktop) {
      setIsDisplay(true)
    } else {
      setIsDisplay(false)
    }
  }, [isDesktop])

  useLayoutEffect(() => {
    try {
      const blogText = document.getElementsByClassName('blog-text')
      const blogTextLength = blogText.length

      Array(blogTextLength).fill().map(
        (_, i) => blogText[i].firstChild.addEventListener('click', onBlogClick),
      )
    } catch (err) {
      console.error('Blog click. GTM is not supported in IE.', err)
    }
  }, [onBlogClick])

  const styleMemo = useMemo(
    () => ({
      box1Marg: { horizontal: 'auto', vertical: '0px' },
      box1Pad: { vertical: 'small' },
      box2Marg: {
        horizontal: 'small',
        bottom: 'small',
      },
      icomoon: { height: '100%', width: '100%' },
      titleSize: isDesktop ? 'medium' : 'small',
      btnStyle: { width: '110px', padding: 0 },
    }),
    [isDesktop],
  )

  const handleAccordion = useCallback(() => {
    setIsDisplay(prevDisplay => !prevDisplay)
  }, [])

  if (!blogsToShow || blogsToShow.length === 0) {
    return null
  }

  return (
    <Box
      id={homeIDs.blogs}
      width="1248px"
      margin={styleMemo.box1Marg}
    >
      <MainSection>
        <HeadingSection margin={styleMemo.box2Marg}>
          <Title
            style={{ fontSize: '20px' }}
            size={styleMemo.titleSize}
            label="Helpful Automotive Resources"
            type="h2"
          />
          <CollapseBox onClick={handleAccordion}>
            <IcoMoon icon={isDisplay ? RemoveIcon : AddIcon} color="#393939" />
          </CollapseBox>
        </HeadingSection>
        {
            isDisplay && (
              <>
                <ResponsiveBox
                  id="inTheGarageBlog"
                  fxHeight={{ large: '54px', other: '43px' }}
                  fxWidth={{ large: '250px', other: '200px' }}
                  fxAlignSelf={{ large: 'start', other: 'center' }}
                  margin={styleMemo.box2Marg}
                >
                  <Box id="GuidesAndArticlesHeading">
                    <a
                      href={blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onBlogClick}
                    >
                      <IcoMoon icon={CarpartsBlogIcon} style={styleMemo.icomoon} />
                    </a>
                  </Box>
                </ResponsiveBox>
                <ResponsiveBox
                  id={homeIDs.blogGrid}
                  fxDirection={{ large: 'row', other: 'column' }}
                  wrap
                  fxStyle={{ large: { width: '80%' }, other: {} }}
                  alignSelf="center"
                >
                  {blogsToShow && blogsToShow.map((data, index) => (
                    <ItemView
                      key={data.link}
                      data={data}
                      colWidth={colWidth}
                      isBot={isBot}
                      index={index}
                      onBlogClick={onBlogClick}
                    />
                  ))}
                </ResponsiveBox>
              </>
            )
          }
      </MainSection>
    </Box>
  )
}

export default function Blog({ dynamicApiData = {} }) {
  const {
    pageType = 'home',
    blogs,
    products = {},
    selectedAttributes = {},
    vehicle = {},
    headings = {},
  } = dynamicApiData
  let attribute = {}
  let limit = 3

  switch (pageType) {
    case 'part':
      attribute = {
        part: (products && products.selectedPart) || '',
      }
      break
    case 'brand':
      attribute = {
        brand: selectedAttributes.brand,
      }
      break
    case 'brand_part':
      attribute = {
        brand: selectedAttributes.brand,
        part: (products && products.selectedPart) || '',
      }
      break
    case 'make':
      attribute = {
        make: selectedAttributes.make,
      }
      break
    case 'make_part':
      attribute = {
        make: selectedAttributes.make,
        part: (products && products.selectedPart) || '',
      }
      break
    case 'make_model':
      attribute = {
        make: selectedAttributes.make,
        model: selectedAttributes.model,
      }
      break
    case 'make_model_part':
      attribute = {
        make: selectedAttributes.make,
        model: selectedAttributes.model,
        part: (products && products.selectedPart) || '',
      }
      break
    case 'part_make_model_year':
      attribute = {
        year: selectedAttributes.year,
        make: selectedAttributes.make,
        model: selectedAttributes.model,
        part: (products && products.selectedPart) || '',
      }
      limit = 3
      break
    case 'year_make_model':
      attribute = {
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
      }
      limit = 3
      break
    case 'first_level_category':
    case 'second_level_category':
      attribute = {
        [pageType]: headings.summary,
      }
      break
      // no default
  }

  return (
    <BlogProvider
      article={blogs}
      limit={limit}
      pageType={pageType}
      attribute={attribute}
      width={380}
      height={180}
    >
      <BlogComponent
        dynamicApiData={blogs}
        pageType={pageType}
      />
    </BlogProvider>
  )
}
